import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import secureLocalStorage from 'react-secure-storage';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { useTheme } from '@mui/material/styles';
import { Box, Grid, Divider, Backdrop, Container, Typography } from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { genericApiToaster } from 'src/services/utils';

import Loader from 'src/components/loaders/Loader';
import DashboardToolbar from 'src/components/ToolBars/DashboardToolbar';

import { useAuth } from '../../hooks/useAuth';
import { fetchStatuses } from '../../services/status.service';
import { fetchTicketsByDate } from '../../services/ticket.service';
import { fetchDepartmentsByIds } from '../../services/department.service';
import DepartmentSelect from '../../components/departmentSelect/DepartmentSelect';
import { BarChart, AppWidgetSummary, AppCurrentSubject } from '../../components/charts';

// ----------------------------------------------------------------------

export default function TicketManagerDashboard() {
  const theme = useTheme();
  const { id: username, user: authUser, departmentIds } = useAuth();

  const [allTickets, setAllTickets] = useState([]); // Master authorized dataset
  const [departments, setDepartments] = useState([]);
  const [statusesList, setStatusesList] = useState([]);
  const [statusNames, setStatusNames] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(0);

  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();

  const start = useMemo(() => new Date(secureLocalStorage.getItem('ticketStart')), []);
  const end = useMemo(() => new Date(secureLocalStorage.getItem('ticketEnd')), []);

  const getStatuses = useCallback(
    (ids) => {
      if (!ids?.length) return;
      genericApiToaster(fetchStatuses, [], '', '', null, null, t, (res) =>
        setStatusesList(
          res.filter((s) => ids.includes(s.departmentId) || s.departmentId === 0)
        )
      );
    },
    [t]
  );

  /**
   * ARCHITECTURAL FIX: Scoped Dataset Resolution
   * Standardized fetch logic matching the Admin/Dept Manager dashboards.
   */
  const getTicketsByDate = useCallback(
    async ({ startDate, endDate, attributeName }) => {
      setIsLoading(true);
      await genericApiToaster(
        fetchTicketsByDate,
        { startDate, endDate, attributeName },
        '',
        '',
        null,
        null,
        t,
        (res) => setAllTickets(res),
        undefined,
        () => setIsLoading(false)
      );
    },
    [t]
  );

  const fetchDepartmentsData = useCallback(() => {
    if (!departmentIds?.length) return;
    genericApiToaster(
      fetchDepartmentsByIds,
      [departmentIds],
      '',
      '',
      null,
      null,
      t,
      setDepartments
    );
  }, [departmentIds, t]);

  const metrics = useMemo(() => {
    const statusMap = Object.fromEntries(statusesList.map((s) => [s.statusId, s]));
    const counts = { open: 0, onhold: 0, resolved: 0, closed: 0 };
    let expired = 0;
    let expiring = 0;
    const now = new Date().getTime();
    const aMap = {};
    const catMap = {};

    // 1. Filter authorized dataset based on UI selection
    const scopedTickets = allTickets.filter(
      (t) =>
        selectedDepartmentId === 0 ||
        Number(t.departmentId) === Number(selectedDepartmentId)
    );

    // 2. Perform Single-Pass Metric Aggregation
    scopedTickets.forEach((ticket) => {
      const statusObj = statusMap[ticket.status_id] || {
        name: 'Unknown',
        systemMappedStatus: 'OPEN',
      };
      const status = statusObj.systemMappedStatus;

      // SLA Logic: Tracking Estimated End Times
      const eTime = new Date(ticket.estimatedEndTime).getTime();
      const sTime = new Date(ticket.estimatedStartTime).getTime();

      if (status === 'OPEN') counts.open += 1;
      if (status === 'ONHOLD') counts.onhold += 1;
      if (status === 'RESOLVED') counts.resolved += 1;
      if (status === 'CLOSED') counts.closed += 1;

      if (eTime < now && status !== 'RESOLVED' && status !== 'CLOSED') expired += 1;
      if (
        status !== 'RESOLVED' &&
        status !== 'CLOSED' &&
        eTime > now &&
        eTime - now <= 0.1 * (eTime - sTime)
      ) {
        expiring += 1;
      }

      // Personnel Logic: Best Performing Agents in Scope
      if (ticket.agentName && ticket.agentName !== 'none') {
        if (!aMap[ticket.assignedTo])
          aMap[ticket.assignedTo] = {
            agent: ticket.agentName,
            agentId: ticket.assignedTo,
            tickets: [],
          };
        aMap[ticket.assignedTo].tickets.push(ticket);
      }

      // Metadata Logic: Category distribution
      if (!catMap[ticket.category])
        catMap[ticket.category] = { category: ticket.category, tickets: [] };
      catMap[ticket.category].tickets.push(ticket);
    });

    // 3. Subject-Status Matrix Mapping
    const categorySubjects = Object.keys(catMap).map((key) => ({
      name: key,
      data: statusesList.map(
        (s) => catMap[key].tickets.filter((tk) => tk.status_id === s.statusId).length
      ),
    }));

    return {
      totalCount: scopedTickets.length,
      ticketStatusCount: counts,
      expiredTickets: expired,
      expiringTickets: expiring,
      ticketsByAgent: Object.values(aMap),
      ticketCategoryBySubject: categorySubjects,
    };
  }, [allTickets, statusesList, selectedDepartmentId]);

  useEffect(() => {
    if (departmentIds?.length > 0) {
      getStatuses(departmentIds);
      fetchDepartmentsData();
    }
  }, [departmentIds, getStatuses, fetchDepartmentsData]);

  useEffect(() => {
    if (statusesList.length > 0) {
      getTicketsByDate({ startDate: start, endDate: end, attributeName: 'created' });
    }
  }, [statusesList, getTicketsByDate, start, end]);

  useEffect(() => {
    setStatusNames(
      statusesList.map((s) => (s.statusId < 6 ? t(`status.${s.name}`) : s.name))
    );
  }, [statusesList, t]);

  return (
    <>
      <Helmet>
        <title> Oryx One Ticketing System </title>
      </Helmet>
      <Container maxWidth="xl">
        <Backdrop
          sx={{
            bgcolor: 'rgb(121 121 121 / 32%)',
            zIndex: 9999,
            backdropFilter: 'blur(4px)',
          }}
          open={isLoading}
        >
          <Loader size={175} hideText />
        </Backdrop>

        <Box sx={{ mb: 5 }}>
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              fontWeight: 900,
              fontSize: '0.65rem',
              letterSpacing: '0.1em',
            }}
          >
            {t('dashboard.greeting').toUpperCase()}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, color: '#212B36' }}>
            {authUser?.givenName || username || 'user'}!
          </Typography>
          <Divider
            sx={{ mt: 2, borderStyle: 'solid', borderColor: 'rgba(0,0,0,0.08)' }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: isSmallScreen ? 'column' : 'row',
            alignItems: 'center',
            width: '100%',
            mb: 4,
          }}
        >
          <DashboardToolbar
            getTicketsByDate={getTicketsByDate}
            start={start}
            end={end}
            setIsLoading={setIsLoading}
          />
          <Box sx={{ mt: isSmallScreen ? 2 : 0, ml: !isSmallScreen ? 2 : 0 }}>
            <DepartmentSelect
              options={
                departments.length > 0
                  ? departments
                  : [{ name: t('attributes.department'), id: 0 }]
              }
              setSelectedDepId={setSelectedDepartmentId}
            />
          </Box>
        </Box>

        <Grid container columns={15} spacing={3}>
          <Grid item xs={7.5} md={3}>
            <AppWidgetSummary
              title={t('dashboard.expiringTickets')}
              color="warning"
              total={metrics.expiringTickets}
              icon="uil:clock-ten"
              sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '16px' }}
            />
          </Grid>
          <Grid item xs={7.5} md={3}>
            <AppWidgetSummary
              title={t('dashboard.expiredTickets')}
              total={metrics.expiredTickets}
              color="error"
              icon="uil:clock-two"
              sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '16px' }}
            />
          </Grid>
          <Grid item xs={7.5} md={3}>
            <AppWidgetSummary
              title={t('dashboard.onHoldTickets')}
              total={metrics.ticketStatusCount.onhold}
              icon="ph:ticket"
              sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '16px' }}
            />
          </Grid>
          <Grid item xs={7.5} md={3}>
            <AppWidgetSummary
              title={t('dashboard.closureRate')}
              total={
                (metrics.ticketStatusCount.resolved / (metrics.totalCount || 1)) * 100
              }
              color="success"
              icon="material-symbols:percent"
              sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '16px' }}
            />
          </Grid>
          <Grid item xs={15} md={3}>
            <AppWidgetSummary
              title={t('dashboard.openingRate')}
              total={
                ((metrics.ticketStatusCount.onhold + metrics.ticketStatusCount.open) /
                  (metrics.totalCount || 1)) *
                100
              }
              color="info"
              icon="material-symbols:percent"
              sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '16px' }}
            />
          </Grid>

          <Grid item xs={15} lg={10}>
            <Box
              sx={{
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: '16px',
                overflow: 'hidden',
              }}
            >
              <BarChart
                title={t('dashboard.bestPerAgents')}
                chartData={metrics.ticketsByAgent
                  .filter((a) =>
                    a.tickets.some(
                      (tk) => tk.statusObject?.systemMappedStatus === 'RESOLVED'
                    )
                  )
                  .sort(
                    (a, b) =>
                      b.tickets.filter(
                        (tk) => tk.statusObject?.systemMappedStatus === 'RESOLVED'
                      ).length -
                      a.tickets.filter(
                        (tk) => tk.statusObject?.systemMappedStatus === 'RESOLVED'
                      ).length
                  )
                  .map((a) => ({
                    label: a.agent,
                    value: a.tickets.filter(
                      (tk) => tk.statusObject?.systemMappedStatus === 'RESOLVED'
                    ).length,
                  }))}
              />
            </Box>
          </Grid>

          <Grid item xs={15} lg={5}>
            <Box
              sx={{
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: '16px',
                overflow: 'hidden',
              }}
            >
              <AppCurrentSubject
                title={t('dashboard.mostReccuringSubjects')}
                chartLabels={statusNames}
                chartData={metrics.ticketCategoryBySubject}
                chartColors={[...Array(statusNames.length)].map(
                  () => theme.palette.text.secondary
                )}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
